import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BookingCancel Component
 * 
 * UPDATED: Now automatically cancels/deletes the booking when payment is cancelled
 * This prevents unpaid bookings from taking up room availability
 */
const BookingCancel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cancelling, setCancelling] = useState(true);
  const [error, setError] = useState(null);
  
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    const cancelBooking = async () => {
      if (!bookingId) {
        setCancelling(false);
        return;
      }

      try {
        // Cancel the booking since payment was cancelled
        await axios.post(`${API}/public/bookings/${bookingId}/cancel`);
        console.log(`✅ Booking ${bookingId} cancelled after payment cancellation`);
      } catch (err) {
        console.error('Error cancelling booking:', err);
        // Don't show error to user - the booking might already be deleted
        // or the user might have refreshed the page
        setError('Unable to cancel booking automatically');
      } finally {
        setCancelling(false);
      }
    };

    cancelBooking();
  }, [bookingId]);

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4">
      <Card className="max-w-md w-full shadow-xl border-0" data-testid="booking-cancel-card">
        <CardContent className="p-8 text-center">
          {cancelling ? (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-blue-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Cancelling Booking...
              </h2>
              <p className="text-gray-600 mb-6">
                Please wait while we cancel your reservation.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-orange-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Booking Cancelled
              </h2>
              <p className="text-gray-600 mb-4">
                Your booking was cancelled. No charges were made to your account.
              </p>
              {bookingId && !error && (
                <p className="text-sm text-gray-500 mb-6">
                  The reservation has been removed from our system.
                </p>
              )}
              <Button 
                onClick={() => navigate('/')} 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                data-testid="back-home-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingCancel;